import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api, unwrap } from "../api/client";
import type { components } from "../api/schema";

type ClockFile = components["schemas"]["ClockFile"];
type Suggestion = components["schemas"]["ClockSuggestion"];
type Lane = components["schemas"]["TimelineLane"];

/** E.g. "+1.234 s", "−0.500 s". */
export const signedSeconds = (ms: number) =>
  `${ms < 0 ? "−" : "+"}${(Math.abs(ms) / 1000).toFixed(3)} s`;

/** From the clock that reads later, whichever way round the pair is kept. */
function describe(s: Suggestion): string {
  const [late, early] = s.offset_ms >= 0 ? [s.b, s.a] : [s.a, s.b];
  const name = (f: ClockFile) => f.source ?? f.original_name;
  return `${named(late)} and ${named(early)} heard the same sounds, but ${name(late)}’s clock reads ${(Math.abs(s.offset_ms) / 1000).toFixed(3)} s later than ${name(early)}’s (${Math.round(s.overlap_s)} s of shared sound, confidence ${s.confidence.toFixed(1)}).`;
}

const named = (f: ClockFile) =>
  `${f.source ?? "Unidentified"} (${f.original_name})`;

/**
 * Clock corrections for the recordings in view (design §6.4): suggestions
 * from recordings that heard the same sounds, for a person to accept or
 * reject, and corrections, which can also be set by hand.
 */
export function ClockPanel({
  range,
  filter,
  lanes,
  canCorrect,
  canMarkReference,
}: {
  range: { from: number; to: number };
  filter: { library?: string; case?: string };
  lanes: Lane[];
  canCorrect: boolean;
  canMarkReference: boolean;
}) {
  const qc = useQueryClient();
  const query = {
    from: new Date(range.from).toISOString(),
    to: new Date(range.to).toISOString(),
    ...filter,
  };
  const clocks = useQuery({
    queryKey: ["clocks", query],
    queryFn: () => unwrap(api.GET("/api/clocks", { params: { query } })),
  });
  const done = () => {
    void qc.invalidateQueries({ queryKey: ["clocks"] });
    void qc.invalidateQueries({ queryKey: ["timeline"] });
  };
  const accept = useMutation({
    mutationFn: ({ id, shift }: { id: string; shift: string }) =>
      unwrap(
        api.POST("/api/clocks/suggestions/{id}/accept", {
          params: { path: { id } },
          body: { shift },
        }),
      ),
    onSuccess: done,
  });
  const reject = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST("/api/clocks/suggestions/{id}/reject", {
          params: { path: { id } },
        }),
      ),
    onSuccess: done,
  });
  const reference = useMutation({
    mutationFn: (f: ClockFile) =>
      unwrap(
        api.PUT("/api/clocks/references", {
          body: {
            library_id: f.library_id,
            source: f.source ?? "",
            reference: !f.reference,
          },
        }),
      ),
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE("/api/files/{id}/clock", { params: { path: { id } } })),
    onSuccess: done,
  });
  const [manual, setManual] = useState({ file: "", seconds: "", reason: "" });
  const set = useMutation({
    mutationFn: () =>
      unwrap(
        api.PUT("/api/files/{id}/clock", {
          params: { path: { id: manual.file } },
          body: {
            offset_ms: Number(manual.seconds) * 1000,
            reason: manual.reason,
          },
        }),
      ),
    onSuccess: () => {
      setManual({ file: "", seconds: "", reason: "" });
      done();
    },
  });
  const open = clocks.data?.suggestions.filter((s) => s.state === "open") ?? [];
  const corrections = clocks.data?.corrections ?? [];
  const files = lanes.flatMap((l) =>
    l.files.map((f) => ({
      id: f.id,
      name: `${l.source ?? "Unidentified"} (${f.original_name})`,
    })),
  );
  const failed = [accept, reject, reference, remove, set].find(
    (m) => m.isError,
  );
  if (
    !clocks.data ||
    (open.length === 0 && corrections.length === 0 && !canCorrect)
  )
    return null;

  const offer = (s: Suggestion) => {
    // Correct the one that isn't reference time, if one is; else either.
    const order = s.suggested_shift === s.a.file_id ? [s.a, s.b] : [s.b, s.a];
    return order.map((f, i) => {
      const other = f.file_id === s.a.file_id ? s.b : s.a;
      // A: B reads offset later than A, so B is corrected by −offset.
      const by = f.file_id === s.b.file_id ? -s.offset_ms : s.offset_ms;
      return (
        <Button
          key={f.file_id}
          size="small"
          variant={i === 0 && s.suggested_shift ? "contained" : "outlined"}
          disabled={!canCorrect || accept.isPending}
          onClick={() => accept.mutate({ id: s.id, shift: f.file_id })}
        >
          Correct {f.source ?? f.original_name} by {signedSeconds(by)}
          {other.reference ? ` (to ${other.source}, reference time)` : ""}
        </Button>
      );
    });
  };

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-label="Clocks"
    >
      <Stack spacing={1.5}>
        <Typography variant="h6" component="h3">
          Clocks
        </Typography>
        {failed?.error && (
          <Alert severity="error">{failed.error.message}</Alert>
        )}
        {open.map((s) => (
          <Paper
            key={s.id}
            variant="outlined"
            sx={{ p: 1.5 }}
            role="group"
            aria-label="Clock suggestion"
          >
            <Typography variant="body2">{describe(s)}</Typography>
            <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap", mt: 1 }}>
              {offer(s)}
              <Button
                size="small"
                disabled={!canCorrect || reject.isPending}
                onClick={() => reject.mutate(s.id)}
              >
                Not the same sounds
              </Button>
              {canMarkReference &&
                [s.a, s.b]
                  .filter((f) => f.source)
                  .map((f) => (
                    <Button
                      key={`ref${f.file_id}`}
                      size="small"
                      onClick={() => reference.mutate(f)}
                      disabled={reference.isPending}
                    >
                      {f.reference
                        ? `${f.source} isn't reference time`
                        : `${f.source} keeps reference time`}
                    </Button>
                  ))}
            </Stack>
          </Paper>
        ))}
        {corrections.length > 0 && (
          <Stack spacing={0.5} role="list" aria-label="Clock corrections">
            {corrections.map((c) => (
              <Stack
                key={c.file.file_id}
                role="listitem"
                direction="row"
                sx={{ gap: 1, alignItems: "center", flexWrap: "wrap" }}
              >
                <Typography variant="body2">
                  {named(c.file)}: {signedSeconds(c.offset_ms)},{" "}
                  {c.method === "audio" ? "matched by sound" : "set by hand"} by{" "}
                  {c.set_by} ({c.reason})
                </Typography>
                {canCorrect && (
                  <Button
                    size="small"
                    onClick={() => remove.mutate(c.file.file_id)}
                    disabled={remove.isPending}
                  >
                    Remove
                  </Button>
                )}
              </Stack>
            ))}
          </Stack>
        )}
        {canCorrect && files.length > 0 && (
          <Stack
            direction="row"
            component="form"
            aria-label="Correct a clock by hand"
            sx={{ gap: 1, flexWrap: "wrap", alignItems: "center" }}
            onSubmit={(e) => {
              e.preventDefault();
              set.mutate();
            }}
          >
            <TextField
              select
              size="small"
              label="Recording"
              value={manual.file}
              onChange={(e) => setManual({ ...manual, file: e.target.value })}
              sx={{ minWidth: 260 }}
            >
              {files.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Correction (s)"
              value={manual.seconds}
              onChange={(e) =>
                setManual({ ...manual, seconds: e.target.value })
              }
              placeholder="-1.5"
              sx={{ width: 140 }}
            />
            <TextField
              size="small"
              label="Reason"
              value={manual.reason}
              onChange={(e) => setManual({ ...manual, reason: e.target.value })}
              sx={{ minWidth: 220 }}
            />
            <Button
              type="submit"
              variant="outlined"
              disabled={
                !manual.file ||
                !manual.reason.trim() ||
                !Number.isFinite(Number(manual.seconds)) ||
                manual.seconds.trim() === "" ||
                set.isPending
              }
            >
              Correct by hand
            </Button>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
